import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Briefcase, CreditCard, Wallet, TrendingUp, TrendingDown, ArrowUpRight,
  ArrowDownRight, UserCog, FolderKanban, FileText, CalendarClock, Banknote,
  ScrollText, ShieldCheck, Plus, Sparkles, Activity, Clock, ChevronRight,
  DollarSign, PieChart as PieChartIcon, Bell, AlertTriangle, Eye, EyeOff,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../context/PermissionsContext";

/* ============================================================ *
 * AP-021 · Enterprise Dashboard v2 · Premium SaaS rebuild.
 * Reusa 100% das APIs existentes; nenhuma mudança de backend.
 * ============================================================ */

const methodLabels = { cash: "Dinheiro", pix: "Pix", bank: "Transferência", card: "Cartão" };
const methodColors = { cash: "#10b981", pix: "#06b6d4", bank: "#8b5cf6", card: "#f59e0b" };

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}
function formatDatePt() {
  return new Date().toLocaleDateString("pt-BR", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });
}

function KpiTile({ label, value, sublabel, icon: Icon, testid, delta, tone = "green" }) {
  const tones = {
    green:  { bg: "bg-emerald-500/10", text: "text-emerald-600", ring: "ring-emerald-500/20", grad: "from-emerald-500 to-emerald-600" },
    blue:   { bg: "bg-sky-500/10",     text: "text-sky-600",     ring: "ring-sky-500/20",     grad: "from-sky-500 to-sky-600" },
    violet: { bg: "bg-violet-500/10",  text: "text-violet-600",  ring: "ring-violet-500/20",  grad: "from-violet-500 to-violet-600" },
    amber:  { bg: "bg-amber-500/10",   text: "text-amber-600",   ring: "ring-amber-500/20",   grad: "from-amber-500 to-amber-600" },
  }[tone];
  const isUp = typeof delta === "number" && delta >= 0;
  return (
    <div
      data-testid={testid}
      className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_2px_10px_rgba(15,23,42,0.04)] hover:shadow-[0_10px_30px_rgba(15,23,42,0.08)] transition-all duration-300 group"
    >
      <div className={`absolute -top-8 -right-8 h-32 w-32 rounded-full ${tones.bg} blur-2xl opacity-70 group-hover:opacity-100 transition-opacity`} />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className={`h-11 w-11 rounded-2xl bg-gradient-to-br ${tones.grad} grid place-items-center shadow-lg shadow-black/5`}>
            <Icon className="h-5 w-5 text-white" strokeWidth={2.2} />
          </div>
          {typeof delta === "number" && (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${isUp ? "text-emerald-700 bg-emerald-50 ring-emerald-500/20" : "text-red-700 bg-red-50 ring-red-500/20"}`}>
              {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {isUp ? "+" : ""}{delta}%
            </span>
          )}
        </div>
        <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
        <p className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900 tabular-nums">{value ?? "—"}</p>
        {sublabel && <p className="mt-1 text-xs text-slate-500">{sublabel}</p>}
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, to, testid }) {
  const body = (
    <div className="rounded-xl border border-slate-200/80 bg-white p-3 flex items-center gap-3 hover:border-primary/40 hover:bg-slate-50/50 transition group cursor-pointer" data-testid={testid}>
      <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0 group-hover:bg-primary group-hover:text-white transition-all">
        <Icon className="h-4 w-4" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 truncate font-semibold">{label}</p>
        <p className="text-sm font-display font-bold tabular-nums text-slate-900">{value ?? "—"}</p>
      </div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function QuickAction({ icon: Icon, label, to, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-xl bg-slate-900 hover:bg-slate-800 active:scale-[0.98] text-white text-sm font-semibold h-11 px-4 shadow-[0_10px_24px_-10px_rgba(15,23,42,0.6)] transition-all"
    >
      <Icon className="h-4 w-4" />
      {label}
    </Link>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const { formatCurrency } = useSettings();
  const { user } = useAuth();
  const { can, role } = usePermissions();
  const isMaster = role === "master_super_admin";
  const canFinance = isMaster || can("payments", "view");
  const canCustomers = isMaster || can("customers", "view");
  const canServices = isMaster || can("services", "view");
  const canProcesses = isMaster || can("processes", "view");
  const canDocuments = isMaster || can("documents", "view");
  const canEmployees = isMaster || can("employees", "view");
  const canPayroll = isMaster || can("payroll", "view");
  const canAppointments = isMaster || can("appointments", "view");
  const canAudit = isMaster || can("audit", "view");
  const canUsers = isMaster || can("users", "view");

  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [partnerSummary, setPartnerSummary] = useState(null);

  // AP-034 · Financial values visibility toggle. Session-persisted so the
  // user stays in the state they chose while navigating. UI-only; no backend
  // call, no impact on business logic, permissions or data models.
  const [showFinancials, setShowFinancials] = useState(() => {
    try {
      const raw = sessionStorage.getItem("jms_show_financials");
      return raw === null ? true : raw === "1";
    } catch { return true; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("jms_show_financials", showFinancials ? "1" : "0"); } catch {}
  }, [showFinancials]);
  const toggleFinancials = () => setShowFinancials((v) => !v);
  const MONEY_MASK = "••••••";
  const fmtMoney = (v) => (showFinancials ? formatCurrency(v) : MONEY_MASK);
  // Chart axis / tooltip formatters honour the same toggle.
  const chartMoneyTip = (v) => (showFinancials ? formatCurrency(v) : MONEY_MASK);
  const chartAxisTick = (v) => {
    if (!showFinancials) return "•••";
    return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v;
  };

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/dashboard/monthly-payments", { params: { months: 6 } })
      .then((r) => setMonthly(r.data)).catch(() => {});
    api.get("/dashboard/outstanding-by-customer", { params: { limit: 6 } })
      .then((r) => setOutstanding(r.data)).catch(() => {});
    api.get("/dashboard/global-stats").then((r) => setGlobalStats(r.data)).catch(() => {});
    // AP-102 · "Repasse a Parceiros · Resumo Financeiro" APENAS para Master.
    // Diretor, Secretária, Financeiro, RH e Funcionário jamais chamam este
    // endpoint (backend também retorna 403). Isso evita leaks financeiros
    // nem em cache/devtools nem no bundle.
    if (isMaster) {
      api.get("/dashboard/partner-revenue-summary").then((r) => setPartnerSummary(r.data)).catch(() => {});
    }
  }, [isMaster]);

  const delta = useMemo(() => {
    if (monthly.length < 2) return null;
    const last = monthly[monthly.length - 1].total;
    const prev = monthly[monthly.length - 2].total;
    if (!prev) return last > 0 ? 100 : 0;
    return Math.round(((last - prev) / prev) * 100);
  }, [monthly]);

  // Method distribution derived from recent payments (for donut)
  const methodDist = useMemo(() => {
    if (!summary?.recent_payments) return [];
    const acc = {};
    for (const p of summary.recent_payments) acc[p.method] = (acc[p.method] || 0) + (p.amount_paid || 0);
    return Object.entries(acc).map(([k, v]) => ({ name: methodLabels[k] || k, value: v, key: k }));
  }, [summary]);

  const firstName = (user?.full_name || user?.name || user?.email || "").split(/[ .@]/)[0] || "Admin";
  const insight = (() => {
    if (!summary) return "Carregando resumo…";
    const pending = summary.outstanding_balance || 0;
    if (pending > 0) return `Você tem ${fmtMoney(pending)} em saldos pendentes para cobrar.`;
    return `Tudo em dia! Nenhum saldo pendente no momento.`;
  })();

  return (
    <div data-testid="dashboard-page" className="pb-10 -mx-6 -mt-6 md:-mx-8 md:-mt-8 px-6 md:px-8 pt-6 md:pt-8 bg-gradient-to-b from-slate-50 via-white to-slate-50 min-h-full">

      {/* ── Greeting Header ─────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-500 capitalize">{formatDatePt()}</p>
          <h1 className="mt-1 font-display font-bold text-3xl md:text-4xl tracking-tight text-slate-900">
            {greeting()}, {firstName} <span className="inline-block animate-pulse">👋</span>
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-xl flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            {insight}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canFinance && <QuickAction icon={Plus} label="Novo Pagamento" to="/payments" testid="qa-new-payment" />}
          {canCustomers && (
            <Link
              to="/customers"
              data-testid="qa-new-customer"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 active:scale-[0.98] text-slate-700 text-sm font-semibold h-11 px-4 shadow-sm transition-all"
            >
              <Users className="h-4 w-4" />
              Novo Cliente
            </Link>
          )}
        </div>
      </div>

      {/* ── KPI Big Cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
        {canCustomers && (
          <KpiTile testid="kpi-customers" label="Total de Clientes" tone="blue"
            value={summary?.total_customers ?? "—"} icon={Users}
            sublabel={globalStats?.active_users ? `${globalStats.active_users} usuários ativos` : ""} />
        )}
        {canServices && (
          <KpiTile testid="kpi-services" label="Serviços Cadastrados" tone="violet"
            value={summary?.total_services ?? "—"} icon={Briefcase}
            sublabel={globalStats?.processes ? `${globalStats.processes} processos ativos` : ""} />
        )}
        {canFinance && (
          <KpiTile testid="kpi-payments" label="Receita Total" tone="green"
            value={summary ? fmtMoney(summary.total_paid) : "—"}
            sublabel={summary ? `${summary.total_payments_count} transações` : ""}
            icon={DollarSign} delta={delta} />
        )}
        {canFinance && (
          <KpiTile testid="kpi-outstanding" label="Saldo Pendente" tone="amber"
            value={summary ? fmtMoney(summary.outstanding_balance) : "—"} icon={Wallet}
            sublabel="Aguardando recebimento" />
        )}
      </div>

      {/* ── AP-102 · Partner Revenue KPIs (Master Super Admin ONLY) ── */}
      {isMaster && partnerSummary && (
        <div className="mt-4" data-testid="partner-revenue-panel">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
              <p className="jms-eyebrow">Repasse a Parceiros · Resumo Financeiro</p>
            </div>
            {/* AP-034 · Global eye toggle for financial values. Controls every
                currency display on the Dashboard (KPIs, tables, charts, tooltips). */}
            <button
              type="button"
              data-testid="toggle-financial-visibility"
              onClick={toggleFinancials}
              aria-pressed={!showFinancials}
              title={showFinancials ? "Ocultar valores financeiros" : "Mostrar valores financeiros"}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 hover:border-primary/40 px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:text-primary shadow-sm transition-all"
            >
              {showFinancials ? (
                <>
                  <Eye className="h-3.5 w-3.5" />
                  <span>Ocultar valores</span>
                </>
              ) : (
                <>
                  <EyeOff className="h-3.5 w-3.5" />
                  <span>Mostrar valores</span>
                </>
              )}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiTile testid="pr-total-received" label="Total Recebido" tone="green" icon={DollarSign}
              value={fmtMoney(partnerSummary.total_amount_received)} sublabel="Receita bruta" />
            <KpiTile testid="pr-partner-payables" label="A Repassar" tone="amber" icon={ArrowDownRight}
              value={fmtMoney(partnerSummary.total_partner_payables)} sublabel="Total devido a parceiros" />
            <KpiTile testid="pr-partner-settled" label="Já Repassado" tone="blue" icon={ArrowUpRight}
              value={fmtMoney(partnerSummary.total_partner_payments_completed)} sublabel="Repasses concluídos" />
            <KpiTile testid="pr-partner-pending" label="Pendente" tone="amber" icon={AlertTriangle}
              value={fmtMoney(partnerSummary.pending_partner_payments)} sublabel="Aguardando repasse" />
            <KpiTile testid="pr-gross-revenue" label="Receita Bruta" tone="green" icon={TrendingUp}
              value={fmtMoney(partnerSummary.gross_revenue)} sublabel="Total antes de repasses" />
            <KpiTile testid="pr-net-revenue" label="Receita Líquida JMS" tone="violet" icon={Wallet}
              value={fmtMoney(partnerSummary.net_jms_revenue)} sublabel="Após descontar parceiros" />
          </div>
        </div>
      )}

      {/* AP-102 · Fallback eye toggle apenas para Master quando o painel de repasse
          ainda não carregou. Evita renderizar o botão global de visibilidade
          financeira para Diretor/Secretária/Financeiro. */}
      {isMaster && !partnerSummary && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            data-testid="toggle-financial-visibility"
            onClick={toggleFinancials}
            aria-pressed={!showFinancials}
            title={showFinancials ? "Ocultar valores financeiros" : "Mostrar valores financeiros"}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 hover:border-primary/40 px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:text-primary shadow-sm transition-all"
          >
            {showFinancials ? (<><Eye className="h-3.5 w-3.5" /><span>Ocultar valores</span></>) : (<><EyeOff className="h-3.5 w-3.5" /><span>Mostrar valores</span></>)}
          </button>
        </div>
      )}

      {/* ── Mini stats strip ─────────────────────────────────────────── */}
      {globalStats && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="global-stats-strip">
          {canCustomers   && globalStats.customers != null &&           <MiniStat testid="gs-customers"    icon={Users}        label="Clientes"       value={globalStats.customers}          to="/customers" />}
          {canEmployees   && globalStats.employees != null &&           <MiniStat testid="gs-employees"    icon={UserCog}      label="Funcionários"   value={globalStats.employees}          to="/hr/employees" />}
          {canProcesses   && globalStats.processes != null &&           <MiniStat testid="gs-processes"    icon={FolderKanban} label="Processos"      value={globalStats.processes}          to="/processes" />}
          {canDocuments   && globalStats.documents != null &&           <MiniStat testid="gs-documents"    icon={FileText}     label="Documentos"     value={globalStats.documents}          to="/documents" />}
          {canAppointments && globalStats.appointments_today != null && <MiniStat testid="gs-appointments" icon={CalendarClock} label="Agenda hoje"   value={globalStats.appointments_today} to="/appointments" />}
          {canPayroll     && globalStats.payroll_periods != null &&     <MiniStat testid="gs-payroll"      icon={Banknote}     label="Competências"   value={globalStats.payroll_periods}    to="/payroll/dashboard" />}
          {canAudit       && globalStats.audit_events_today != null &&  <MiniStat testid="gs-audit"        icon={ScrollText}   label="Auditoria hoje" value={globalStats.audit_events_today} to="/settings/audit" />}
          {canUsers       && globalStats.active_users != null &&        <MiniStat testid="gs-users"        icon={ShieldCheck}  label="Usuários"       value={globalStats.active_users}       to="/users" />}
        </div>
      )}

      {/* ── Revenue chart + Recent activity ─────────────────────────────────── */}
      {canFinance && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Revenue Area chart 2/3 */}
          <div data-testid="revenue-chart-panel" className="lg:col-span-2 relative rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_2px_10px_rgba(15,23,42,0.04)] overflow-hidden">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Receita</p>
                <h2 className="mt-1 font-display font-bold text-xl tracking-tight text-slate-900">
                  {summary ? fmtMoney(summary.total_paid) : "—"}
                </h2>
                <p className="mt-1 text-xs text-slate-500 flex items-center gap-1">
                  <Activity className="h-3 w-3" />
                  Últimos 6 meses · {monthly.length} pontos
                </p>
              </div>
              <div className="flex items-center gap-2">
                {typeof delta === "number" && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${delta >= 0 ? "text-emerald-700 bg-emerald-50 ring-emerald-500/20" : "text-red-700 bg-red-50 ring-red-500/20"}`}>
                    {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {delta >= 0 ? "+" : ""}{delta}% vs. anterior
                  </span>
                )}
                <span className="rounded-lg bg-slate-900 text-white text-[10px] font-semibold px-2.5 py-1">6M</span>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthly} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#16a34a" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#16a34a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false}
                    tickFormatter={chartAxisTick} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12, boxShadow: "0 10px 30px rgba(15,23,42,0.08)" }}
                    formatter={chartMoneyTip}
                  />
                  <Area type="monotone" dataKey="total" stroke="#16a34a" strokeWidth={3} fill="url(#revenueFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Payment methods donut */}
          <div data-testid="method-distribution-panel" className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Métodos de Pagamento</p>
                <h3 className="mt-1 font-display font-bold text-lg text-slate-900">Distribuição</h3>
              </div>
              <PieChartIcon className="h-4 w-4 text-slate-400" />
            </div>
            <div className="h-48 relative">
              {methodDist.length === 0 ? (
                <div className="h-full grid place-items-center text-xs text-slate-400">Sem dados</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={methodDist} innerRadius={45} outerRadius={72} paddingAngle={4} dataKey="value" nameKey="name">
                      {methodDist.map((e) => (<Cell key={e.key} fill={methodColors[e.key] || "#16a34a"} />))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                      formatter={chartMoneyTip}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="mt-2 space-y-1.5">
              {methodDist.map((e) => (
                <div key={e.key} className="flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-2 text-slate-700">
                    <span className="h-2 w-2 rounded-full" style={{ background: methodColors[e.key] || "#16a34a" }} />
                    {e.name}
                  </span>
                  <span className="font-semibold tabular-nums text-slate-900">{fmtMoney(e.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Outstanding + Recent activity ─────────────────────────────────── */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Outstanding bar chart */}
        {canFinance && (
          <div data-testid="outstanding-panel" className="lg:col-span-2 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Pipeline Financeiro</p>
                <h2 className="mt-1 font-display font-bold text-lg text-slate-900">Saldos Pendentes</h2>
                <p className="text-xs text-slate-500">Top clientes com maior débito</p>
              </div>
              <Link to="/balance" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80">
                Ver todos <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="h-64">
              {outstanding.length === 0 ? (
                <div className="h-full grid place-items-center text-sm text-slate-400 gap-2">
                  <ShieldCheck className="h-8 w-8 text-emerald-500" />
                  <span>Nenhum saldo pendente. Tudo em dia ✓</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={outstanding} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false}
                      tickFormatter={chartAxisTick} />
                    <YAxis type="category" dataKey="customer_name" stroke="#64748b" fontSize={11}
                      tickLine={false} axisLine={false} width={110} />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                      formatter={chartMoneyTip}
                    />
                    <Bar dataKey="outstanding" fill="#16a34a" radius={[0, 8, 8, 0]} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        )}

        {/* Recent activity / notifications side card */}
        <div data-testid="activity-panel" className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary/10 grid place-items-center">
                <Bell className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Atividade Recente</p>
                <h3 className="font-display font-bold text-slate-900">Últimos eventos</h3>
              </div>
            </div>
          </div>
          <ul className="space-y-3">
            {(!summary || summary.recent_payments.length === 0) ? (
              <li className="text-sm text-slate-400 py-8 text-center">Sem atividade recente</li>
            ) : (
              summary.recent_payments.slice(0, 5).map((p) => (
                <li key={p.id} className="flex items-start gap-3 group">
                  <div className="mt-0.5 h-8 w-8 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center shrink-0 ring-2 ring-white group-hover:ring-emerald-100 transition-all">
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 truncate">{p.customer_name}</p>
                    <p className="text-xs text-slate-500 truncate">{p.service_name}</p>
                    <div className="mt-0.5 flex items-center justify-between gap-2">
                      <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" />{p.payment_date}</span>
                      <span className="text-xs font-semibold text-emerald-600 tabular-nums">{fmtMoney(p.amount_paid)}</span>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
          {canFinance && (
            <Link to="/payments" className="mt-4 inline-flex w-full items-center justify-center gap-1 rounded-xl border border-slate-200 hover:border-primary/40 hover:bg-primary/5 py-2 text-xs font-semibold text-slate-700 hover:text-primary transition-all">
              Ver histórico completo <ChevronRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      </div>

      {/* ── Recent payments premium table ─────────────────────────────────── */}
      {canFinance && (
        <div className="mt-6 rounded-2xl border border-slate-200/80 bg-white shadow-[0_2px_10px_rgba(15,23,42,0.04)] overflow-hidden" data-testid="recent-payments-panel">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Transações</p>
              <h2 className="mt-1 font-display font-bold text-lg text-slate-900">Pagamentos Recentes</h2>
            </div>
            <Link to="/payments" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80">
              Ver todos <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-payments-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left font-semibold text-[10px] uppercase tracking-widest text-slate-500 px-6 py-3">Cliente</th>
                  <th className="text-left font-semibold text-[10px] uppercase tracking-widest text-slate-500 px-6 py-3">Serviço</th>
                  <th className="text-right font-semibold text-[10px] uppercase tracking-widest text-slate-500 px-6 py-3">Valor</th>
                  <th className="text-right font-semibold text-[10px] uppercase tracking-widest text-slate-500 px-6 py-3">Saldo</th>
                  <th className="text-left font-semibold text-[10px] uppercase tracking-widest text-slate-500 px-6 py-3">Data</th>
                  <th className="text-left font-semibold text-[10px] uppercase tracking-widest text-slate-500 px-6 py-3">Método</th>
                </tr>
              </thead>
              <tbody>
                {(!summary || summary.recent_payments.length === 0) && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-400">Nenhum pagamento recente</td>
                  </tr>
                )}
                {summary?.recent_payments.map((p) => (
                  <tr key={p.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors">
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 text-slate-700 grid place-items-center text-[10px] font-bold uppercase ring-2 ring-white">
                          {(p.customer_name || "??").slice(0, 2)}
                        </div>
                        <span className="font-medium text-slate-900">{p.customer_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 text-slate-600">{p.service_name}</td>
                    <td className="px-6 py-3.5 text-right font-mono tabular-nums">
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                        <ArrowUpRight className="h-3.5 w-3.5" />{fmtMoney(p.amount_paid)}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-right font-mono tabular-nums">
                      <span className={p.remaining_balance > 0 ? "text-red-600 font-semibold" : "text-slate-400"}>
                        {fmtMoney(p.remaining_balance)}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-slate-500 text-xs">{p.payment_date}</td>
                    <td className="px-6 py-3.5">
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1"
                        style={{
                          background: `${methodColors[p.method] || "#16a34a"}15`,
                          color: methodColors[p.method] || "#16a34a",
                          boxShadow: `inset 0 0 0 1px ${methodColors[p.method] || "#16a34a"}30`,
                        }}
                      >
                        {methodLabels[p.method] || p.method}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
